import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlatformModal } from './PlatformModal.js';

/**
 * Void-invoice modal (Phase D-2). Mirrors the server Zod in
 * apps/api/src/routes/platform/invoices.ts (POST .../:id/void): a customer-
 * facing void reason (rendered on the record) PLUS the internal audit reason.
 * Voiding is terminal and works from any non-voided state (draft/issued/overdue/
 * paid → voided; a paid void is a refund). A second void is rejected (409
 * INVOICE_VOIDED), surfaced via errorMessage.
 */
const schema = z.object({
  voidReason: z
    .string()
    .trim()
    .min(1, 'Void reason is required')
    .max(2000, 'Void reason must be 2000 characters or fewer'),
  reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
});
type Values = z.infer<typeof schema>;

export function VoidInvoiceModal({
  open,
  onClose,
  invoiceNumber,
  isPaid = false,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  invoiceNumber: string;
  /** When the invoice is already paid, voiding is a refund — warn the operator. */
  isPaid?: boolean;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (body: { reason: string; voidReason: string }) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { voidReason: '', reason: '' } });

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title="Void invoice"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="void-invoice-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Voiding…' : 'Void invoice'}
          </button>
        </>
      }
    >
      <form id="void-invoice-form" onSubmit={handleSubmit((v) => onConfirm(v))}>
        <p className="text-sm text-gray-700 mb-3">
          Void invoice <span className="font-medium">{invoiceNumber}</span>. This is terminal — a voided invoice
          cannot be reinstated.
          {isPaid ? (
            <span className="text-red-600">
              {' '}
              This invoice is already paid; voiding it records a refund.
            </span>
          ) : null}
        </p>

        <label htmlFor="void-reason-customer" className="block text-sm font-medium text-gray-700 mb-1">
          Void reason (on the record)
        </label>
        <textarea
          id="void-reason-customer"
          {...register('voidReason')}
          disabled={isSubmitting}
          rows={2}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Why this invoice is being voided…"
        />
        {errors.voidReason ? <p className="mt-1 text-sm text-red-600">{errors.voidReason.message}</p> : null}

        <label htmlFor="void-reason-audit" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason (audit trail)
        </label>
        <textarea
          id="void-reason-audit"
          {...register('reason')}
          disabled={isSubmitting}
          rows={2}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Internal note for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
